import type { SetupState } from '@tandemone/types'
import { Check, ShieldCheck, Sparkles } from 'lucide-react'
import { Link } from 'react-router'

import { useSetupState } from '@/domains/setup'
import { QA_SKIP_CERTS_LABEL } from '@/domains/supplier-onboarding'

import {
  type ChecklistItem,
  type SectionProgress,
  getSharedSteps,
  getSupplierSections
} from './checklist-items'

function headerCopy(setup: SetupState): {
  heading: string
  description: string
} {
  if (setup.isSupplier) {
    return {
      heading: `Welcome${setup.companyName ? `, ${setup.companyName}` : ''}`,
      description:
        'Three short sections. Complete each so buyers can evaluate your company with confidence.'
    }
  }
  if (setup.isBuyer) {
    return {
      heading: 'Buyer profile',
      description:
        'Complete each section so suppliers can evaluate your company with confidence.'
    }
  }
  return {
    heading: 'Onboarding',
    description: 'Start with your company information to get going.'
  }
}

export function OnboardingHub() {
  const { setupState } = useSetupState()
  if (!setupState) return null

  const { heading, description } = headerCopy(setupState)
  const sharedSteps = getSharedSteps(setupState)
  const sections = setupState.isSupplier ? getSupplierSections(setupState) : []
  const qualitySkippedByCert =
    setupState.supplier?.qualityAssessmentSkippedByCert ?? false

  const sharedDone = sharedSteps.filter(step => step.complete).length
  const totalSteps =
    sharedSteps.length +
    sections.reduce((total, section) => total + section.total, 0)
  const doneSteps =
    sharedDone + sections.reduce((done, section) => done + section.complete, 0)
  const overallPercent =
    totalSteps > 0 ? Math.round((doneSteps / totalSteps) * 100) : 0

  return (
    <div className='flex w-full flex-col gap-6'>
      <div>
        <h1 className='font-display text-[22px] leading-[1.1] tracking-[-0.4px] break-words text-ink sm:text-[26px]'>
          {heading}
        </h1>
        <p className='mt-1 text-[13px] text-ink-soft'>{description}</p>
      </div>

      {totalSteps > 0 && (
        <ProgressBar
          percent={overallPercent}
          done={doneSteps}
          total={totalSteps}
          isBuyer={setupState.isBuyer}
        />
      )}

      <div className='flex flex-col gap-3'>
        {sharedSteps.map(step => (
          <SharedStepCard key={step.id} item={step} />
        ))}
        {sections.map(section => {
          const skippedByCert =
            section.info.id === 'quality_assessment' && qualitySkippedByCert
          return (
            <SectionCard
              key={section.info.id}
              section={section}
              skippedByCert={skippedByCert}
            />
          )
        })}
      </div>
    </div>
  )
}

/** a shared company-level step (Company information, Contacts) rendered in the
 * same list as the role sections, mirroring their structure with a single
 * step circle.
 */
function SharedStepCard({ item }: { item: ChecklistItem }) {
  const Icon = item.icon
  return (
    <Link
      to={item.ctaHref}
      className='group flex flex-col gap-4 rounded-2xl border border-border bg-page p-4 transition-all active:border-accent/40 active:bg-cream/40 sm:p-5 md:p-6 md:hover:-translate-y-px md:hover:border-accent/40 md:hover:shadow-sm'
    >
      <div className='flex items-start gap-4'>
        <div className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-accent-light text-accent dark:text-accent-mid'>
          <Icon className='size-6' />
        </div>
        <div className='min-w-0 flex-1'>
          <p className='text-[16px] font-semibold text-ink'>{item.title}</p>
          <p className='mt-1 text-[13px] text-ink-soft'>{item.description}</p>
        </div>
        <StatusPill status={item.complete ? 'complete' : 'not_started'} />
      </div>

      <ul className='hidden flex-wrap gap-x-4 gap-y-2 pl-16 text-[12px] md:flex'>
        <li className='flex items-center gap-1.5'>
          <span
            className={`flex size-3.5 shrink-0 items-center justify-center rounded-full border ${
              item.complete
                ? 'border-accent bg-accent text-white'
                : 'border-border bg-page'
            }`}
          >
            {item.complete && <Check className='size-2' strokeWidth={3} />}
          </span>
          <span
            className={
              item.complete ? 'text-ink-muted line-through' : 'text-ink-soft'
            }
          >
            {item.title}
          </span>
        </li>
      </ul>
    </Link>
  )
}

function ProgressBar({
  percent,
  done,
  total,
  isBuyer
}: {
  percent: number
  done: number
  total: number
  isBuyer: boolean
}) {
  const allDone = done === total
  return (
    <div className='rounded-2xl border border-border bg-cream/40 p-4'>
      <div className='mb-2 flex items-center justify-between'>
        <span className='text-[12px] font-medium text-ink-muted'>
          {allDone ? (
            <span className='inline-flex items-center gap-1.5 text-accent dark:text-accent-mid'>
              <Sparkles className='size-3.5' /> All sections complete
            </span>
          ) : (
            'Onboarding status'
          )}
        </span>
        <span className='text-[12px] font-medium text-ink-muted'>
          {percent}%
        </span>
      </div>
      {allDone && (
        <p className='mb-2 text-[12px] text-ink-soft'>
          {isBuyer
            ? 'Once our team approves your account, you can start exploring the supplier network and send your first RFI.'
            : 'Our team will review your profile and let you know once buyers can discover you on the platform.'}
        </p>
      )}
      <div className='h-1.5 w-full overflow-hidden rounded-full bg-border'>
        <div
          className='h-full rounded-full bg-accent transition-all'
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  )
}

function SectionCard({
  section,
  skippedByCert
}: {
  section: SectionProgress
  skippedByCert: boolean
}) {
  const Icon = section.info.icon

  if (skippedByCert) {
    return (
      <div className='flex items-start gap-4 rounded-2xl border border-border bg-page p-5 md:p-6'>
        <div className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-accent-light text-accent dark:text-accent-mid'>
          <Icon className='size-6' />
        </div>
        <div className='min-w-0 flex-1'>
          <p className='text-[16px] font-semibold text-ink'>
            {section.info.title}
          </p>
          <p className='mt-1 text-[13px] text-ink-soft'>
            You hold {QA_SKIP_CERTS_LABEL}, so this section is auto-completed.
          </p>
        </div>
        <StatusPill status='skipped' />
      </div>
    )
  }

  const allDone = section.total > 0 && section.complete === section.total
  const notStarted = section.complete === 0
  const status = allDone
    ? 'complete'
    : notStarted
      ? 'not_started'
      : 'in_progress'

  const content = (
    <>
      <div className='flex items-start gap-4'>
        <div className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-accent-light text-accent dark:text-accent-mid'>
          <Icon className='size-6' />
        </div>
        <div className='min-w-0 flex-1'>
          <p className='text-[16px] font-semibold break-words text-ink'>
            {section.info.title}
          </p>
          <p className='mt-1 text-[13px] text-ink-soft'>
            {section.info.description}
          </p>
          <div className='mt-2 sm:hidden'>
            <StatusPill status={status} />
          </div>
        </div>
        <div className='hidden sm:block'>
          <StatusPill status={status} />
        </div>
      </div>

      <ul className='hidden flex-wrap gap-x-4 gap-y-2 pl-16 text-[12px] md:flex'>
        {section.items.map(item => (
          <li key={item.id} className='flex items-center gap-1.5'>
            <span
              className={`flex size-3.5 shrink-0 items-center justify-center rounded-full border ${
                item.complete
                  ? 'border-accent bg-accent text-white'
                  : 'border-border bg-page'
              }`}
            >
              {item.complete && <Check className='size-2' strokeWidth={3} />}
            </span>
            <span
              className={
                item.complete ? 'text-ink-muted line-through' : 'text-ink-soft'
              }
            >
              {item.title}
            </span>
          </li>
        ))}
      </ul>
    </>
  )

  if (!section.clickable) {
    return (
      <div className='flex flex-col gap-4 rounded-2xl border border-border bg-page p-4 sm:p-5 md:p-6'>
        {content}
      </div>
    )
  }

  return (
    <Link
      to={section.info.href}
      className='group flex flex-col gap-4 rounded-2xl border border-border bg-page p-4 transition-all active:border-accent/40 active:bg-cream/40 sm:p-5 md:p-6 md:hover:-translate-y-px md:hover:border-accent/40 md:hover:shadow-sm'
    >
      {content}
    </Link>
  )
}

function StatusPill({
  status
}: {
  status: 'complete' | 'in_progress' | 'not_started' | 'skipped'
}) {
  if (status === 'skipped') {
    return (
      <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-light px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent dark:text-accent-mid'>
        <ShieldCheck className='size-3' strokeWidth={3} /> Skipped
      </span>
    )
  }
  if (status === 'complete') {
    return (
      <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-light px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-accent dark:text-accent-mid'>
        <Check className='size-3' strokeWidth={3} /> Done
      </span>
    )
  }
  if (status === 'in_progress') {
    return (
      <span className='inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:bg-amber-950 dark:text-amber-200'>
        In progress
      </span>
    )
  }
  return (
    <span className='inline-flex shrink-0 items-center rounded-full bg-cream px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-muted'>
      Not started
    </span>
  )
}
